import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { SuiteHero } from '@/components/client/SuiteHero';
import { GuidedEmptyState } from '@/components/client/GuidedEmptyState';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight, Brain, CalendarClock,
  CheckCircle2, Download, Loader2, Minus, Rocket, ShieldCheck, Sparkles,
  TrendingUp, Users, XCircle,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { clientApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { bizDate } from '@/lib/dates';
import { toast } from 'sonner';
import ClimaraLaunchWizard from '@/components/client/ClimaraLaunchWizard';
import ClimaraCampaignConsole from '@/components/shared/ClimaraCampaignConsole';
import StrategicAnalysisDialog from '@/components/client/StrategicAnalysisDialog';
import StrategicRequestsHistory from '@/components/client/StrategicRequestsHistory';
import { ClientClimaraLaunch } from '@/components/client/ClientClimaraLaunch';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';

const bandFor = (avg, max) => {
  if (avg == null || !max) return { es: 'Sin datos', en: 'No data', color: '#94a3b8' };
  const pct = avg / max;
  if (pct >= 0.8) return { es: 'Saludable', en: 'Healthy', color: '#16a34a' };
  if (pct >= 0.65) return { es: 'Estable', en: 'Stable', color: '#0891b2' };
  if (pct >= 0.5) return { es: 'Atención', en: 'Attention', color: '#d97706' };
  return { es: 'Crítico', en: 'Critical', color: '#dc2626' };
};

function Kpi({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card className="p-4 shadow-card" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-lg inline-flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${color || '#0f172a'}18`, color: color || '#0f172a' }}>
          <Icon className="h-4.5 w-4.5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground truncate">{label}</p>
          <p className="text-xl font-bold leading-tight" style={color ? { color } : undefined}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function ClientClimara() {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const locale = isES ? 'es' : 'en';

  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [report, setReport] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [waves, setWaves] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [trackedCampaignId, setTrackedCampaignId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [strategicOpen, setStrategicOpen] = useState(false);
  const [decisionNote, setDecisionNote] = useState('');
  const [deciding, setDeciding] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reps, wv, tb, reqs, camps] = await Promise.all([
        clientApi.climaraReports().catch(() => []),
        clientApi.climaraWaves().catch(() => ({ items: [] })),
        clientApi.climaraTeamBuilding().catch(() => ({ items: [] })),
        clientApi.climaraCampaignRequests().catch(() => ({ items: [] })),
        clientApi.climaraCampaigns().catch(() => ({ items: [] })),
      ]);
      setReports(reps || []);
      setWaves(wv?.items || []);
      setProposals(tb?.items || []);
      setRequests(reqs?.items || []);
      const campList = camps?.items || [];
      setCampaigns(campList);
      setTrackedCampaignId((prev) => {
        if (prev && campList.some((c) => c.id === prev)) return prev;
        const firstActive = campList.find((c) => c.status === 'active');
        return firstActive?.id || null;
      });
      if ((reps || []).length) setSelectedId((prev) => prev || reps[0].id);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        const d = await clientApi.climaraReport(selectedId);
        setReport(d);
      } catch (e) { toast.error(formatApiError(e)); }
      setAnalysis(null);
      setAnalysisLoading(true);
      try {
        setAnalysis(await clientApi.climaraExecutiveAnalysis(selectedId, locale));
      } catch { /* fallback silently */ }
      setAnalysisLoading(false);
    })();
  }, [selectedId, locale]);

  const payload = report?.payload || {};
  const scaleMax = payload.scale?.max || 5;
  const overall = payload.overall_avg;
  const band = bandFor(overall, scaleMax);
  const totals = payload.totals || {};
  const dims = useMemo(
    () => (payload.dimensions || []).filter((d) => d.avg != null),
    [payload]);
  const depts = useMemo(() => (payload.department_breakdown || []).map((d) => {
    const dvals = (d.dimensions || []).map((x) => x.avg).filter((v) => v != null);
    return {
      label: d.label || d.department_name || '—',
      avg: dvals.length ? +(dvals.reduce((a, b) => a + b, 0) / dvals.length).toFixed(2) : (d.avg ?? null),
      respondents: d.respondents ?? d.n ?? null,
    };
  }), [payload]);
  const riskDepts = depts.filter((d) => d.avg != null && d.avg / scaleMax < 0.55);

  const waveIdx = waves.findIndex((w) => w.report_id === selectedId);
  const prevWave = waveIdx > 0 ? waves[waveIdx - 1] : null;
  const delta = prevWave && overall != null && prevWave.overall_avg != null
    ? +(overall - prevWave.overall_avg).toFixed(2) : null;

  const pendingReq = requests.find((r) => r.status === 'pending');
  const changesReq = requests.find((r) => r.status === 'changes_requested');

  const campaignTracking = campaigns.length > 0 && (
    <Card className="p-6 shadow-card" data-testid="climara-client-campaigns">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" />
          <h3 className="font-display font-bold text-lg tracking-tight">
            {isES ? 'Seguimiento de campañas' : 'Campaign tracking'}
          </h3>
        </div>
        {campaigns.length > 1 && (
          <Select value={trackedCampaignId || ''} onValueChange={setTrackedCampaignId}>
            <SelectTrigger className="w-64 h-8 text-xs" data-testid="climara-client-campaign-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} · {c.wave_label || ''} {c.status === 'active' ? (isES ? '(activa)' : '(active)') : (isES ? '(cerrada)' : '(closed)')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {trackedCampaignId ? (
        <ClimaraCampaignConsole key={trackedCampaignId} campaignId={trackedCampaignId} mode="client" onChanged={load} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {isES ? 'Selecciona una campaña para ver su seguimiento.' : 'Select a campaign to view its tracking.'}
        </p>
      )}
    </Card>
  );

  const changesBanner = changesReq && (
    <Card className="p-4 shadow-card border-sky-200 bg-sky-50/50 flex items-start gap-3" data-testid="climara-changes-requested">
      <AlertTriangle className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold">
          {isES ? 'Corporate Talent Hub solicitó cambios en tu medición' : 'Corporate Talent Hub requested changes to your wave'}
        </p>
        <p className="text-xs text-muted-foreground">
          “{changesReq.name}” ({changesReq.wave_label}){changesReq.changes_note ? ` — ${changesReq.changes_note}` : ''}
        </p>
        <Button size="sm" variant="outline" className="mt-2" onClick={() => setWizardOpen(true)}
                data-testid="climara-changes-relaunch-btn">
          <Rocket className="h-3.5 w-3.5 mr-1.5" />
          {isES ? 'Enviar medición corregida' : 'Submit corrected wave'}
        </Button>
      </div>
    </Card>
  );

  // V5.1 §E — executive Team Building PDF (available once CTH proposes it)
  const downloadTbPdf = async (pid) => {
    try {
      const r = await api.get(`/client/climara/team-building/${pid}/pdf?locale=${isES ? 'es' : 'en'}`,
        { responseType: 'blob', timeout: 90000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `team-building-${pid.slice(0, 8)}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decide = async (pid, decision) => {
    setDeciding(pid + decision);
    try {
      await clientApi.climaraTeamBuildingDecision(pid, decision, decisionNote || null);
      toast.success(decision === 'accepted'
        ? (isES ? 'Propuesta aceptada — Corporate Talent Hub coordinará las actividades' : 'Proposal accepted — Corporate Talent Hub will coordinate the activities')
        : (isES ? 'Propuesta declinada' : 'Proposal declined'));
      setDecisionNote('');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeciding(null);
  };

  if (loading) {
    return (
      <>
        <Topbar title="Climara" subtitle={t('clientClimara.subtitle', { defaultValue: 'Organizational climate' })} />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="climara-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />{t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }

  // ---------------- EMPTY STATE ----------------
  if (!reports.length) {
    return (
      <>
        <Topbar title="Climara" subtitle={t('clientClimara.subtitle', { defaultValue: 'Organizational climate' })} />
        <div className="p-6 lg:p-8 space-y-6" data-testid="client-climara">
          <div className="flex flex-wrap items-center gap-2">
            <ClientClimaraLaunch onLaunched={() => window.location.reload()} />
            <RequestHMSupport suite="climara" origin="/client/climara" />
          </div>
          <SuiteHero suite="climara" icon={Activity} />
          {pendingReq && (
            <Card className="p-4 shadow-card border-amber-200 bg-amber-50/50 flex items-start gap-3" data-testid="climara-pending-request">
              <CalendarClock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">
                  {isES ? 'Medición en revisión por Corporate Talent Hub' : 'Wave under Corporate Talent Hub review'}
                </p>
                <p className="text-xs text-muted-foreground">
                  “{pendingReq.name}” ({pendingReq.wave_label}) · {pendingReq.invited_count} {isES ? 'invitados' : 'invited'}
                </p>
              </div>
            </Card>
          )}
          {changesBanner}
          {campaignTracking}
          {campaigns.length > 0 ? (
            (() => {
              const cam = campaigns.find((c) => c.status === 'active') || campaigns[0];
              const k = cam?.kpis || {};
              return (
                <Card className="p-6 shadow-card border-l-4 border-l-primary" data-testid="climara-campaign-in-progress">
                  <p className="font-display font-bold text-lg tracking-tight">
                    {isES ? 'Campaña en curso' : 'Campaign in progress'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isES
                      ? `${k.completed ?? 0} de ${k.invited ?? 0} participantes han completado la encuesta (${k.rate_pct ?? 0}% de participación).`
                      : `${k.completed ?? 0} of ${k.invited ?? 0} participants completed the survey (${k.rate_pct ?? 0}% participation).`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {isES
                      ? 'El reporte ejecutivo de clima aparecerá en este panel cuando Corporate Talent Hub lo genere y publique. Puedes seguir el avance de la campaña en el seguimiento de arriba.'
                      : 'The executive climate report will appear on this panel once Corporate Talent Hub generates and releases it. You can follow the campaign progress in the tracking section above.'}
                  </p>
                </Card>
              );
            })()
          ) : (
          <Card className="p-8 shadow-card" data-testid="climara-empty">
            <GuidedEmptyState
              icon={Activity}
              title={t('guidedEmpty.climara.title')}
              why={t('guidedEmpty.climara.why')}
              steps={[1, 2, 3, 4].map((n) => t(`guidedEmpty.climara.step${n}`))}
              cta={(
                <Button onClick={() => setWizardOpen(true)} data-testid="climara-empty-launch-btn">
                  <Rocket className="h-4 w-4 mr-1.5" />
                  {isES ? 'Lanzar mi primera medición' : 'Launch my first wave'}
                </Button>
              )}
            />
          </Card>
          )}
        </div>
        <ClimaraLaunchWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onSubmitted={load} />
      </>
    );
  }

  // ---------------- EXECUTIVE DASHBOARD ----------------
  return (
    <>
      <Topbar
        title="Climara"
        subtitle={isES ? 'Panel ejecutivo de clima organizacional' : 'Executive climate decision panel'}
        actions={(
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedId || ''} onValueChange={setSelectedId}>
              <SelectTrigger className="w-52 h-9" data-testid="climara-wave-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {reports.map((r) => {
                  const w = waves.find((x) => x.report_id === r.id);
                  return (
                    <SelectItem key={r.id} value={r.id}>
                      {(isES ? 'Reporte publicado — ' : 'Published report — ') + (w?.wave_label || r.period || r.campaign_name || '—')}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" data-testid="climara-export-executive-pdf"
                    onClick={() => window.open(clientApi.climaraExecutivePdfUrl(selectedId, locale), '_blank')}>
              <Download className="h-4 w-4 mr-1.5" />{isES ? 'PDF Ejecutivo' : 'Executive PDF'}
            </Button>
            <Button size="sm" className="bg-gradient-primary hover:opacity-90" data-testid="climara-launch-wizard-btn"
                    onClick={() => setWizardOpen(true)}>
              <Rocket className="h-4 w-4 mr-1.5" />{isES ? 'Nueva medición' : 'New wave'}
            </Button>
          </div>
        )}
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="client-climara">
        <div className="flex flex-wrap items-center gap-2">
          <ClientClimaraLaunch onLaunched={() => window.location.reload()} />
          <RequestHMSupport suite="climara" origin="/client/climara" />
        </div>
        {/* FIX 17/18 — the published deliverable is explicit and one click away */}
        {(() => {
          const pub = reports.find((r) => r.id === selectedId);
          return pub ? (
          <Card className="p-4 shadow-card border-emerald-200 bg-emerald-50/50 flex flex-wrap items-center gap-3" data-testid="climara-client-report-published">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-[14rem]">
              <p className="text-sm font-semibold">
                {isES ? 'Informe ejecutivo publicado' : 'Executive report published'}
                {pub.released_at ? ` · ${bizDate(pub.released_at, locale)}` : ''}
              </p>
              <p className="text-xs text-muted-foreground">
                {isES
                  ? 'Este panel muestra el informe publicado por Corporate Talent Hub. También puedes descargarlo.'
                  : 'This panel shows the report published by Corporate Talent Hub. You can also download it.'}
              </p>
            </div>
            <Button variant="outline" size="sm" data-testid="climara-client-report-pdf-btn"
                    onClick={() => window.open(clientApi.climaraExecutivePdfUrl(selectedId, locale), '_blank')}>
              <Download className="h-4 w-4 mr-1.5" />{isES ? 'Descargar PDF' : 'Download PDF'}
            </Button>
          </Card>
          ) : null;
        })()}
        {pendingReq && (
          <Card className="p-3 shadow-card border-amber-200 bg-amber-50/50 flex items-center gap-2 text-xs" data-testid="climara-pending-request">
            <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
            {isES ? 'Nueva medición en revisión:' : 'New wave under review:'} “{pendingReq.name}” ({pendingReq.wave_label})
          </Card>
        )}
        {changesBanner}

        {/* Campaign tracking — operational participation console */}
        {campaignTracking}

        {/* KPI band */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Activity} label={isES ? 'Clima organizacional' : 'Climate score'}
               value={`${overall ?? '—'} / ${scaleMax}`} sub={isES ? band.es : band.en}
               color={band.color} testid="climara-kpi-score" />
          <Kpi icon={Users} label={isES ? 'Participación' : 'Participation'}
               value={`${totals.rate_pct ?? '—'}%`}
               sub={`${totals.responded ?? 0}/${totals.invited ?? 0} ${isES ? 'respuestas' : 'responses'}`}
               color={(totals.rate_pct || 0) >= 70 ? '#16a34a' : '#d97706'} testid="climara-kpi-participation" />
          <Kpi icon={AlertTriangle} label={isES ? 'Departamentos en riesgo' : 'Departments at risk'}
               value={riskDepts.length}
               sub={riskDepts.map((d) => d.label).slice(0, 2).join(', ') || (isES ? 'Ninguno' : 'None')}
               color={riskDepts.length ? '#dc2626' : '#16a34a'} testid="climara-kpi-risk" />
          <Kpi icon={delta == null ? Minus : delta >= 0 ? ArrowUpRight : ArrowDownRight}
               label={isES ? 'Tendencia vs. ola anterior' : 'Trend vs previous wave'}
               value={delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta}`}
               sub={prevWave ? `${prevWave.wave_label}` : (isES ? 'Primera medición' : 'First wave')}
               color={delta == null ? '#64748b' : delta >= 0 ? '#16a34a' : '#dc2626'} testid="climara-kpi-trend" />
        </div>

        {/* Management next steps */}
        <Card className="p-6 shadow-card border-l-4 border-l-primary" data-testid="climara-next-steps">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-5 w-5 text-primary" />
            <h3 className="font-display font-bold text-lg tracking-tight">
              {isES ? '¿Qué debe hacer la dirección ahora?' : 'What should management do next?'}
            </h3>
            {/* Founder correction batch §1 — no client-facing AI labeling. */}
          </div>
          {analysisLoading ? (
            <div className="flex items-center text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {isES ? 'Generando análisis ejecutivo…' : 'Generating executive analysis…'}
            </div>
          ) : analysis ? (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed" data-testid="climara-exec-summary">{analysis.summary}</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{isES ? 'Recomendaciones' : 'Recommendations'}</p>
                  <ul className="space-y-1.5">
                    {(analysis.recommendations || []).slice(0, 4).map((r, i) => (
                      <li key={i} className="text-xs flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />{r}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{isES ? 'Acciones inmediatas' : 'Immediate actions'}</p>
                  <ul className="space-y-1.5">
                    {(analysis.immediate_actions || []).map((r, i) => (
                      <li key={i} className="text-xs flex gap-1.5"><ArrowUpRight className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />{r}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{isES ? 'Riesgos estratégicos' : 'Strategic risks'}</p>
                  <ul className="space-y-1.5">
                    {(analysis.risks || []).slice(0, 3).map((r, i) => (
                      <li key={i} className="text-xs flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => setStrategicOpen(true)} data-testid="climara-request-strategic">
                  {isES ? 'Solicitar análisis estratégico' : 'Request strategic analysis'}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{isES ? 'Análisis no disponible.' : 'Analysis unavailable.'}</p>
          )}
        </Card>

        {/* Item 1 (polish sprint) — published professional interpretation */}
        {report?.interpretation_note?.text && (
          <Card className="p-6 shadow-card border-l-4 border-l-amber-600" data-testid="climara-client-interpretation">
            <h3 className="font-display font-bold text-lg tracking-tight mb-2">
              {isES ? 'Interpretación profesional de Corporate Talent Hub' : 'Professional interpretation by Corporate Talent Hub'}
            </h3>
            {report.interpretation_note.text.split(/\n+/).filter(Boolean).map((p, i) => (
              <p key={i} className="text-sm leading-relaxed mb-2"
                 data-testid={i === 0 ? 'climara-client-interp-text' : undefined}>{p}</p>
            ))}
            {report.interpretation_note.published_at && (
              <p className="text-xs text-muted-foreground mt-1">
                {isES ? 'Publicada el' : 'Published on'} {bizDate(report.interpretation_note.published_at, locale)}
              </p>
            )}
          </Card>
        )}

        {/* Dimensions + trend */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 shadow-card" data-testid="climara-dimensions">
            <h3 className="font-display font-bold text-base tracking-tight mb-4">{isES ? 'Dimensiones del clima' : 'Climate dimensions'}</h3>
            {dims.length === 0 ? (
              <p className="text-sm text-muted-foreground">{isES ? 'Sin dimensiones.' : 'No dimensions.'}</p>
            ) : (
              <div className="space-y-3">
                {dims.map((d) => {
                  const b = bandFor(d.avg, scaleMax);
                  return (
                    <div key={d.code || d.label} data-testid={`climara-dim-${d.code || d.label}`}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">{d.label}</span>
                        <span style={{ color: b.color }} className="font-semibold">{d.avg} / {scaleMax}</span>
                      </div>
                      <Progress value={(d.avg / scaleMax) * 100} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="p-6 shadow-card" data-testid="climara-trend">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-primary" />
              <h3 className="font-display font-bold text-base tracking-tight">{isES ? 'Historial de mediciones' : 'Measurement history'}</h3>
            </div>
            {waves.length < 2 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                {isES
                  ? 'La comparación de tendencias aparecerá cuando publiques tu segunda medición.'
                  : 'Trend comparison will appear once your second wave is released.'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={waves.map((w) => ({ name: w.wave_label, score: w.overall_avg, part: w.rate_pct }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, scaleMax]} tick={{ fontSize: 11 }} width={28} />
                  <RTooltip />
                  <Line type="monotone" dataKey="score" stroke="#0f172a" strokeWidth={2} dot={{ r: 4 }} name={isES ? 'Clima' : 'Climate'} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Department comparison */}
        <Card className="p-6 shadow-card" data-testid="climara-departments">
          <h3 className="font-display font-bold text-base tracking-tight mb-4">{isES ? 'Comparación por departamento' : 'Department comparison'}</h3>
          {depts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{isES ? 'Sin datos por departamento.' : 'No department data.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2">{isES ? 'Departamento' : 'Department'}</th>
                    <th className="text-center py-2">{isES ? 'Respuestas' : 'Respondents'}</th>
                    <th className="text-left py-2 w-1/3">{isES ? 'Promedio' : 'Average'}</th>
                    <th className="text-left py-2">{isES ? 'Estado' : 'Status'}</th>
                  </tr>
                </thead>
                <tbody>
                  {depts.map((d) => {
                    const b = bandFor(d.avg, scaleMax);
                    return (
                      <tr key={d.label} className="border-b last:border-0" data-testid={`climara-dept-${d.label}`}>
                        <td className="py-2 font-medium">{d.label}</td>
                        <td className="py-2 text-center tabular-nums">{d.respondents ?? '—'}</td>
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <Progress value={d.avg != null ? (d.avg / scaleMax) * 100 : 0} className="h-1.5 flex-1" />
                            <span className="tabular-nums font-semibold w-8" style={{ color: b.color }}>{d.avg ?? '—'}</span>
                          </div>
                        </td>
                        <td className="py-2">
                          <Badge variant="outline" style={{ color: b.color, borderColor: `${b.color}55` }} className="text-xs">
                            {isES ? b.es : b.en}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Team Building proposals */}
        <Card className="p-6 shadow-card" data-testid="climara-team-building">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-5 w-5 text-primary" />
            <h3 className="font-display font-bold text-lg tracking-tight">Team Building</h3>
          </div>
          {proposals.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="climara-tb-empty">
              {isES
                ? 'Cuando Corporate Talent Hub analice tus resultados, aquí verás propuestas de actividades de team building alineadas a tus dimensiones más débiles.'
                : 'Once Corporate Talent Hub analyses your results, team building proposals aligned to your weakest dimensions will appear here.'}
            </p>
          ) : proposals.map((p) => (
            <div key={p.id} className="border rounded-lg p-4 mb-3 last:mb-0" data-testid={`climara-tb-${p.id}`}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-sm font-semibold">
                  {isES ? 'Propuesta de Corporate Talent Hub' : 'Corporate Talent Hub proposal'}
                  <span className="text-xs text-muted-foreground font-normal ml-2">{bizDate(p.proposed_at, locale)}</span>
                </p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadTbPdf(p.id)}
                          data-testid={`climara-tb-pdf-${p.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1" />{isES ? 'Descargar PDF' : 'Download PDF'}
                  </Button>
                  <Badge variant="outline" className={`text-xs ${p.status === 'accepted' ? 'text-emerald-600 border-emerald-300' : p.status === 'declined' ? 'text-rose-600 border-rose-300' : 'text-amber-600 border-amber-300'}`}>
                    {p.status === 'proposed' ? (isES ? 'Pendiente de decisión' : 'Awaiting decision')
                      : p.status === 'accepted' ? (isES ? 'Aceptada' : 'Accepted') : (isES ? 'Declinada' : 'Declined')}
                  </Badge>
                </div>
              </div>
              {p.advisor_notes && <p className="text-xs text-muted-foreground mb-3">{p.advisor_notes}</p>}
              <div className="grid md:grid-cols-2 gap-3">
                {(p.activities || []).map((a, i) => (
                  <div key={i} className="rounded-md border bg-muted/30 p-3 space-y-1" data-testid={`climara-tb-activity-${i}`}>
                    <p className="text-sm font-semibold">{a.title}</p>
                    <p className="text-xs text-muted-foreground">{a.objective}</p>
                    {(a.description || a.recommended_activity) && (
                      <p className="text-xs text-muted-foreground">{a.description || a.recommended_activity}</p>
                    )}
                    <p className="text-xs"><span className="font-medium">{isES ? 'Impacto esperado:' : 'Expected impact:'}</span> {a.expected_impact || a.expected_benefits || '—'}</p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {(a.target_dimensions || a.dimensions_addressed || []).map((dd) => <Badge key={dd} variant="secondary" className="text-xs">{dd}</Badge>)}
                    </div>
                    <p className="text-xs text-muted-foreground pt-1">
                      {[a.audience || a.target_group, a.duration || a.suggested_duration, a.frequency].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                ))}
              </div>
              {p.status === 'proposed' && (
                <div className="mt-3 space-y-2">
                  <Textarea rows={2} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
                            placeholder={isES ? 'Comentario para Corporate Talent Hub (opcional)…' : 'Note for Corporate Talent Hub (optional)…'}
                            data-testid={`climara-tb-note-${p.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => decide(p.id, 'accepted')} disabled={!!deciding}
                            data-testid={`climara-tb-accept-${p.id}`}>
                      {deciding === p.id + 'accepted' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />{isES ? 'Aceptar propuesta' : 'Accept proposal'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decide(p.id, 'declined')} disabled={!!deciding}
                            data-testid={`climara-tb-decline-${p.id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" />{isES ? 'Declinar' : 'Decline'}
                    </Button>
                  </div>
                </div>
              )}
              {p.status !== 'proposed' && (
                <div className="mt-2 space-y-1" data-testid={`climara-tb-client-status-${p.id}`}>
                  {p.client_note && (
                    <p className="text-xs text-muted-foreground">{isES ? 'Tu comentario:' : 'Your note:'} {p.client_note}</p>
                  )}
                  {p.status === 'accepted' && (
                    <p className="text-xs text-muted-foreground">
                      {isES ? 'Estado del seguimiento:' : 'Follow-up status:'}{' '}
                      <span className="font-medium text-foreground">
                        {{
                          pendiente_contacto: isES ? 'Corporate Talent Hub te contactará próximamente' : 'Corporate Talent Hub will contact you soon',
                          propuesta_enviada: isES ? 'Propuesta formal enviada' : 'Formal proposal sent',
                          en_coordinacion: isES ? 'En coordinación con Corporate Talent Hub' : 'In coordination with Corporate Talent Hub',
                          contratado: isES ? 'Contratado' : 'Contracted',
                          no_continuara: isES ? 'No continuará' : 'Will not continue',
                        }[p.followup_status] || (isES ? 'Corporate Talent Hub te contactará próximamente' : 'Corporate Talent Hub will contact you soon')}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>

        {/* Anonymity + strategic history */}
        <Card className="p-4 shadow-card flex items-start gap-3 bg-emerald-50/40 border-emerald-200/60" data-testid="climara-anon-banner">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            {isES
              ? 'Modelo de anonimato Climara: el seguimiento de participación es operativo (quién respondió) y visible para responsables de campaña; las respuestas individuales son estrictamente confidenciales. Todos los resultados, análisis y reportes son 100% anónimos y agregados por departamento.'
              : 'Climara anonymity model: participation tracking is operational (who responded) and visible to campaign owners; individual answers are strictly confidential. All results, analysis and PDFs are 100% anonymous and aggregated by department.'}
          </p>
        </Card>
        <StrategicRequestsHistory />
      </div>

      <ClimaraLaunchWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onSubmitted={load} />
      <StrategicAnalysisDialog open={strategicOpen} onOpenChange={setStrategicOpen} />
    </>
  );
}
